// ========================== Hero.jsx ==========================
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n.js";

function Hero({ showPopup }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showLangPopup, setShowLangPopup] = useState(false);
  const [user, setUser] = useState(null);

  // RESPONSIVE
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // LOAD USER (non-blocking)
  useEffect(() => {
    const data = localStorage.getItem("user");
    if (data) setUser(JSON.parse(data));
  }, []);

  // LANGUAGE POPUP
  useEffect(() => {
    const lang = localStorage.getItem("lang");
    if (!lang) {
      setShowLangPopup(true);
    } else {
      i18n.changeLanguage(lang);
    }
  }, []);

  const chooseLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
    setShowLangPopup(false);
  };

  return (
    <section
      style={{
        minHeight: "70vh",
        position: "relative",
        overflow: "visible",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "50px 14px" : "90px 24px"
      }}
    >
      {/* LANGUAGE POPUP */}
      {showLangPopup && (
        <div style={overlayStyle}>
          <div style={popupCard}>
            <h2 style={{ marginBottom: "20px" }}>🌐 Select Language</h2>

            <button onClick={() => chooseLanguage("en")} style={langBtn}>
              🇬🇧 English
            </button>

            <button onClick={() => chooseLanguage("si")} style={langBtn}>
              🇱🇰 සිංහල
            </button>

            <button onClick={() => chooseLanguage("ta")} style={langBtn}>
              🇮🇳 தமிழ்
            </button>
          </div>
        </div>
      )}

      {/* LOGIN POPUP + CLOWN */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: isMobile ? "80px" : "90px",
            right: isMobile ? "10px" : "40px",
            zIndex: 9999
          }}
        >
          {/* CLOWN */}
          <img
            src="/clown.png"
            alt="clown"
            loading="lazy"
            style={{
              position: "absolute",
              top: isMobile ? "-35px" : "-55px",
              right: isMobile ? "0px" : "10px",
              width: isMobile ? "60px" : "85px",
              zIndex: 1000,
              pointerEvents: "none",
              animation: "clownFloat 2.5s ease-in-out infinite"
            }}
          />

          {/* POPUP */}
          <div
            style={{
              minWidth: isMobile ? "180px" : "250px",
              padding: "14px",
              borderRadius: "20px",
              background:
                "linear-gradient(135deg, rgba(147,51,234,0.35), rgba(59,130,246,0.28))",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.18)",
              boxShadow: "0 12px 35px rgba(0,0,0,0.28)",
              animation: "slideIn 0.45s ease"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ fontSize: "24px", animation: "wave 1.2s infinite" }}>
                👋
              </div>

              <div>
                <div style={{ color: "#fff", fontWeight: "700" }}>
                  Hi {user?.full_name?.split(" ")[0]}
                </div>
                <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px" }}>
                  Welcome back
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GLOW */}
      <div style={softBlob(1, isMobile)} />
      <div style={softBlob(2, isMobile)} />

      {/* MAIN CARD */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: "1150px",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr",
          gap: "50px",
          padding: "60px",
          borderRadius: "30px",
          background: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(12px)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.25)"
        }}
      >
        {/* LEFT */}
        <div>
          <span style={badgeStyle}>🎓 {t("platform")}</span>

          <h1 style={titleStyle}>{t("title")}</h1>

          <p style={descStyle}>{t("desc")}</p>

          <button onClick={() => navigate("/mcq")} style={btnStyle}>
            🚀 {t("button")}
          </button>

          <p style={trustStyle}>{t("trusted")} 🇱🇰</p>
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            style={{
              width: "100%",
              maxWidth: isMobile ? "220px" : "380px",
              borderRadius: "24px",
              boxShadow: "0 25px 70px rgba(0,0,0,0.35)",
              objectFit: "cover"
            }}
          >
            <source
              src="/Education Student GIF by ikeja.mp4"
              type="video/mp4"
            />
          </video>
        </div>
      </div>

      <style>{animations}</style>
    </section>
  );
}

/* STYLES */
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.55)",
  zIndex: 99999,
  display: "flex",
  justifyContent: "center",
  alignItems: "center"
};

const popupCard = {
  background: "#fff",
  padding: "30px",
  borderRadius: "20px",
  width: "320px",
  textAlign: "center"
};

const langBtn = {
  display: "block",
  width: "100%",
  marginBottom: "12px",
  padding: "14px",
  border: "none",
  borderRadius: "12px",
  fontWeight: "700",
  background: "linear-gradient(135deg,#4F46E5,#9333EA)",
  color: "#fff"
};

const badgeStyle = {
  display: "inline-block",
  marginBottom: "12px",
  padding: "6px 14px",
  borderRadius: "999px",
  background: "#EEF2FF",
  color: "#4F46E5",
  fontWeight: "700"
};

const titleStyle = {
  fontSize: "clamp(34px, 5vw, 52px)",
  fontWeight: "800",
  marginBottom: "16px",
  color: "#111827"
};

const descStyle = {
  fontSize: "16px",
  color: "#374151",
  marginBottom: "20px"
};

const btnStyle = {
  padding: "15px 48px",
  borderRadius: "14px",
  border: "none",
  fontWeight: "700",
  background: "linear-gradient(135deg, #4F46E5, #9333EA)",
  color: "#fff",
  cursor: "pointer"
};

const trustStyle = {
  marginTop: "10px",
  fontSize: "12px",
  color: "#6B7280"
};

const animations = `
@keyframes float{
  0%,100%{transform:translateY(0)}
  50%{transform:translateY(-10px)}
}
@keyframes wave{
  0%{transform:rotate(0deg)}
  50%{transform:rotate(20deg)}
  100%{transform:rotate(0deg)}
}
@keyframes slideIn{
  from{opacity:0;transform:translateX(40px);}
  to{opacity:1;transform:translateX(0);}
}
@keyframes clownFloat{
  0%,100%{transform:translateY(0)}
  50%{transform:translateY(-12px)}
}
`;

const softBlob = (type, isMobile) => ({
  position: "absolute",
  width: isMobile ? "160px" : type === 1 ? "400px" : "280px",
  height: isMobile ? "160px" : type === 1 ? "400px" : "280px",
  borderRadius: "50%",
  background:
    type === 1
      ? "rgba(165,180,252,0.4)"
      : "rgba(124,58,237,0.3)",
  filter: "blur(40px)"
});

export default Hero;